/**
 * Signature Types and Enums for Abstract Factory Pattern
 *
 * Architecture:
 * - SignType: USB, HSM, CA (Abstract layer 1 - loại ký)
 * - SignProvider: Viettel, VNPT, Easy, Oceantech (Abstract layer 2 - nhà cung cấp)
 */

// ========================================
// Sign Type - Loại ký số
// ========================================
export enum SignType {
  USB = 'USB',       // USB Token ký cục bộ
  HSM = 'HSM',       // Hardware Security Module ký server
  CA = 'CA',         // Remote CA ký từ xa
  IMAGE = 'IMAGE',   // Chữ ký ảnh (fallback)
}

// ========================================
// Sign Provider - Nhà cung cấp chữ ký số
// Có thể mở rộng thêm provider mới bằng cách thêm vào enum
// ========================================
export enum SignProvider {
  VIETTEL = 'VIETTEL',
  VNPT = 'VNPT',
  FPT = 'FPT',
  MYSIGN = 'MYSIGN',       // MySign CA
  ICORP = 'ICORP',         // iCorp HSM
  EASY = 'EASY',           // EasyCA
  BKAV = 'BKAV',           // BKAV CA
  NACENCOMM = 'NACENCOMM', // Nacencomm
  OCEANTECH = 'OCEANTECH', // Ký đơn giản Oceantech (fallback)
}

// ========================================
// Signing Result
// ========================================
export interface SigningResult {
  success: boolean;
  signedData?: string;      // base64 encoded signed data
  certificate?: string;     // Certificate info
  error?: string;
  metadata?: {
    signerName?: string;
    signedAt?: Date;
    serialNumber?: string;
    issuer?: string;
  };
}

// ========================================
// Signing Options
// ========================================
export interface SigningOptions {
  reason?: string;
  location?: string;
  contactInfo?: string;
  /** For PDF: page number to place visual signature */
  page?: number;
  /** For PDF: position of visual signature */
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// ========================================
// XML Signing Options
// ========================================
export interface XmlSigningOptions {
  signatureType?: 'enveloped' | 'enveloping' | 'detached';
  tagToSign?: string;
  canonicalizationMethod?: string;
}

// ========================================
// Batch Signing Item
// ========================================
export interface BatchSigningItem {
  content: string;          // base64 encoded content
  fileName: string;
  metadata?: Record<string, any>;
}

export interface BatchSigningResult {
  success: boolean;
  results: {
    fileName: string;
    signedData?: string;
    error?: string;
  }[];
}

// ========================================
// Certificate Info
// ========================================
export interface CertificateInfo {
  serialNumber: string;
  subject: string;
  issuer: string;
  validFrom: Date;
  validTo: Date;
  isValid: boolean;
}

// ========================================
// Provider Configuration
// ========================================
export interface ProviderConfig {
  signType: SignType;
  signProvider: SignProvider;
  /** Domain/URL của plugin hoặc server */
  domain?: string;
  /** Port của plugin hoặc server */
  port?: number;
  /** API key nếu cần */
  apiKey?: string;
  /** Các config khác */
  options?: Record<string, any>;
}

// ========================================
// Provider Code Format: "PROVIDER-TYPE"
// Examples: MYSIGN-CA, VIETTEL-USB, ICORP-HSM, VNPT-CA
// ========================================

/**
 * Parse provider code từ metadata (field5)
 * Format: "PROVIDER-TYPE" hoặc "PROVIDER"
 *
 * Examples:
 *   "MYSIGN-CA"    → { type: CA, provider: MYSIGN }
 *   "VIETTEL-USB"  → { type: USB, provider: VIETTEL }
 *   "ICORP-HSM"    → { type: HSM, provider: ICORP }
 *   "VNPT-CA"      → { type: CA, provider: VNPT }
 *   "MYSIGN"       → { type: CA, provider: MYSIGN } (default type = CA)
 *   ""             → { type: IMAGE, provider: OCEANTECH } (fallback)
 */
export function parseProviderCode(providerCode: string): { type: SignType; provider: SignProvider } {
  const normalized = providerCode?.toUpperCase().trim() || '';

  // Empty → fallback to IMAGE
  if (!normalized || normalized === 'IMAGE') {
    return { type: SignType.IMAGE, provider: SignProvider.OCEANTECH };
  }

  // Parse format "PROVIDER-TYPE"
  const parts = normalized.split('-');
  const providerPart = parts[0];
  const typePart = parts[1] || 'CA'; // Default to CA if no type specified

  // Map provider string to enum
  const provider = mapToSignProvider(providerPart);

  // Map type string to enum
  const type = mapToSignType(typePart);

  return { type, provider };
}

/**
 * Map string to SignProvider enum
 */
function mapToSignProvider(providerStr: string): SignProvider {
  const providerMap: Record<string, SignProvider> = {
    'VIETTEL': SignProvider.VIETTEL,
    'VNPT': SignProvider.VNPT,
    'FPT': SignProvider.FPT,
    'MYSIGN': SignProvider.MYSIGN,
    'ICORP': SignProvider.ICORP,
    'EASY': SignProvider.EASY,
    'BKAV': SignProvider.BKAV,
    'NACENCOMM': SignProvider.NACENCOMM,
    'OCEANTECH': SignProvider.OCEANTECH,
  };

  return providerMap[providerStr] || SignProvider.OCEANTECH;
}

/**
 * Map string to SignType enum
 */
function mapToSignType(typeStr: string): SignType {
  const typeMap: Record<string, SignType> = {
    'USB': SignType.USB,
    'HSM': SignType.HSM,
    'CA': SignType.CA,
    'IMAGE': SignType.IMAGE,
  };

  return typeMap[typeStr] || SignType.CA;
}
